// ElevationProfile — SVG-Höhenprofil mit aktueller Position als Marker.
//
// Responsive: füllt Container-Breite aus (SVG mit preserveAspectRatio), Höhe
// kommt als Prop. Bei 100 Samples und typischen Breiten bis 400px reicht
// das völlig — ein Sample pro 4 Pixel.
//
// Optionaler Scrub: wenn `onScrub` gesetzt ist, kann der User mit dem Finger
// (oder Maus) übers Profil ziehen — wir berechnen die Position in Metern
// entlang des Tracks und reichen sie nach oben weiter. MapPage rendert daraus
// einen Marker auf der Karte, sodass der User sieht "wo auf dem Trail ist
// dieser Höhenpunkt eigentlich?". Beim Loslassen wird onScrub(null) gerufen,
// damit der Marker wieder verschwindet.

import type { CSSProperties } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { TrailElevationData } from '../lib/useTrailElevation';
import type { CruxPoint } from '@adtu/shared';
import { HazardTriangleIcon } from './CruxToggle';
// Wave 14ca-fix64: pro-Segment-Faerbung des Profils + Scrub-Reveal des
// aktuellen Trail-Names (Custom-Tour-Feature).
import type { ElevationSegment } from '../lib/routeSegmentMap';
import { findSegmentAt } from '../lib/routeSegmentMap';
import { isEverestEasterEggId } from '../lib/everestEasterEgg';
import { useElevationZoomLevels } from '../lib/useUiSettings';

/** Wave 14ew: Zoom-Stufen-Label. <1000m → "750 m", ab 1000m → "1 km" /
 *  "1.5 km" (trailing .0 entfernt). */
function formatZoomLabel(m: number): string {
  if (m < 1000) return `${Math.round(m)} m`;
  return `${(m / 1000).toFixed(1).replace(/\.0$/, '')} km`;
}

export interface ElevationProfileProps {
  /**
   * Wave 14dr: Trail-ID für Easter-Egg-Display-Overrides.
   *
   * Wenn die ID auf das Everest-Easter-Egg matched, wird das Endpunkt-Label
   * (rechts unter dem Profil) IMMER als "8850 m" gerendert — egal was die
   * Samples sagen. Das ist die letzte Verteidigungslinie gegen SRTM-Gipfel-
   * Unterschätzung (alle DEM-Quellen liefern für die Gipfel-Koordinate
   * ~8685 m statt der echten 8850 m / offiziell 8848.86 m). Pipeline-Bypass
   * + paranoid clamp + ID-bump + IDB-purge sind die anderen 4 Schichten;
   * diese 5. Schicht ist Brute-Force im Render-Pfad selbst und kann nicht
   * mehr durch Cache-Races, alte Bundles oder DEM-Failures umgangen werden.
   *
   * Optional damit andere Aufrufer (RunDetailPage, CreateCustomPage) den
   * Prop nicht angeben müssen.
   */
  trailId?: string | null;
  data: TrailElevationData;
  /** Meter entlang Track vom Start aus (wo ist der Rider?). null = kein Marker. */
  progressMeters: number | null;
  /** Perpendikulare Distanz zum Track in Metern. Ab 50m+ zeigen wir den Marker
   *  gedimmt an (Rider ist nicht wirklich auf dem Trail). */
  offTrackMeters?: number | null;
  /** Aktuelle GPS-Höhe in Metern (optional). Wenn gesetzt, überschreibt sie die
   *  aus dem Profil interpolierte Höhe in der Label-Zeile — GPS ist typischerweise
   *  genauer (±5-30m) als ein SRTM-basiertes Profil, daher bevorzugt. Bei null
   *  fällt das Widget auf die Profil-Höhe am Progress-Punkt zurück. */
  gpsAltitude?: number | null;
  /** Höhe des SVG in px. Default 80 — unauffällig aber noch lesbar. */
  heightPx?: number;
  /** Zeigt zusätzlich Distance-Labels "2.3 km gefahren · 5.5 km Rest". */
  showDistanceLabels?: boolean;
  /**
   * Wenn gesetzt: User kann mit dem Finger über das Profil ziehen. Beim
   * Drücken/Bewegen rufen wir onScrub(meter-vom-Start). Beim Loslassen oder
   * Verlassen onScrub(null). Wenn null/undefined: kein Scrubbing.
   */
  onScrub?: (progressMeters: number | null) => void;
  /**
   * Aktuelle Scrub-Position (Meter vom Track-Start). Wenn gesetzt, zeichnen
   * wir einen Scrub-Cursor (vertikale Linie + Dot) an dieser Position. Das
   * hilft dem User: er sieht im Profil DIREKT wo sein Finger ist (das Map-
   * Marker-Update kann je nach Render-Cycle leicht verzögert sein).
   */
  scrubMeters?: number | null;
  /**
   * Schlüsselstellen (Crux-Points) auf diesem Trail. Werden als kleine
   * Gefahr-Dreiecke oberhalb der Profil-Linie an ihrer Distanz-Position
   * eingezeichnet. Leer / undefined → kein Overlay.
   *
   * Die Marker-Y-Position sitzt knapp oberhalb der Höhenlinie am jeweiligen
   * X-Punkt; bei sehr kurzen Tracks kann das Dreieck den Top-Pad berühren,
   * was ok ist (immer noch lesbar).
   */
  cruxPoints?: CruxPoint[];
  /**
   * Wave 14ca-fix64: pro-Segment-Faerbung des Profils.
   *
   * Bei Custom Tours wollen wir die Trail-Difficulty-Farben in den
   * Bereichen sehen wo der gespeicherte Trail-Track auf der Route liegt
   * (User-Wunsch: "Höhenprofil-Sections nach Trail-Difficulty einfaerben").
   * Wenn `segments` gesetzt ist, rendern wir die Profil-Linie segment-
   * weise in den jeweiligen Farben statt durchgehend monochrom.
   *
   * Format: routeSegmentMap.routeSegmentsToElevationSegments() liefert die
   * Range-Liste (von/bis Meter + Farbe + trailId).
   */
  segments?: ElevationSegment[];
  /**
   * Wave 14ca-fix64: Callback wenn der User scrubt UND ein Segment unter
   * dem Finger ist — nuetzlich fuer Scrub-Reveal des Trail-Names.
   * Wird mit `null` aufgerufen wenn entweder kein Scrub aktiv ist oder
   * die Scrub-Position auf einem nicht-Trail-Segment (Connector/Bridge)
   * liegt.
   */
  onSegmentScrub?: (segment: ElevationSegment | null) => void;
}

/**
 * Wenn der User weiter als dieser Schwellwert vom Track weg ist, zeigen wir
 * KEINEN Progress mehr ("X km gefahren") — die Zahl wäre sinnlos weil der
 * User offensichtlich gar nicht auf diesem Trail ist. Er hat den Trail halt
 * nur auf der Karte angeklickt um ihn zu inspizieren (Planungs-Modus).
 *
 * 75m ist großzügiger als der 50m-Dimm-Threshold für den Marker: erst ab 75m
 * blenden wir den Progress komplett aus. Das gibt GPS-Drift + Track-
 * Simplifizierung etwas Spielraum, ohne bei klar-nicht-auf-Trail zu lügen.
 */
const ON_TRACK_THRESHOLD_METERS = 75;

export function ElevationProfile({
  trailId = null,
  data,
  progressMeters,
  offTrackMeters,
  gpsAltitude = null,
  heightPx = 80,
  showDistanceLabels = true,
  onScrub,
  scrubMeters = null,
  cruxPoints,
  segments,
  onSegmentScrub,
}: ElevationProfileProps) {
  // Wave 14dr: 5. Verteidigungslinie für den Everest-Joke. Wenn das Easter-
  // Egg aktiv ist, zwingen wir das End-Label (rechts unter dem Profil) auf
  // 8850 m — egal was die Samples sagen. Verhindert dass irgendein nicht-
  // deployter Bypass / Cache-Race / DEM-Lookup den Gipfel auf SRTM-Wert
  // ~8685 m drückt.
  const isEverest = isEverestEasterEggId(trailId);
  const { elevations, distances, totalMeters, source } = data;
  const svgRef = useRef<SVGSVGElement>(null);
  // Wrapper-Div-Ref: wir befestigen die Pointer-Handler hier statt am SVG.
  // iOS Safari hat zig Bugs mit setPointerCapture auf SVG-Elementen — auf
  // einem ordinären HTMLDivElement läuft es deutlich zuverlässiger.
  const wrapperRef = useRef<HTMLDivElement>(null);
  const scrubActiveRef = useRef(false);
  // Cleanup-Funktion für aktive window-Listener — wird beim nächsten
  // pointerdown überschrieben und beim Unmount gerufen damit nichts leakt.
  const scrubCleanupRef = useRef<(() => void) | null>(null);

  // ---------------------------------------------------------------------
  // Wave 14cf — Zoom-Carousel.
  //
  // Auf langen Touren (z. B. 73 km) ist die volle-Strecke-Ansicht für
  // die nächsten Höhenmeter unbrauchbar — alles wird zu einer matschigen
  // Linie. Der User kann durch 1-Sekunden-Long-Press das Höhenprofil
  // auf einen kürzeren Ausschnitt zoomen. Die Reihenfolge ist ein
  // Carousel: full → 1 km → 5 km → full → 1 km → …
  //
  // Window-Verhalten: zentriert um die GPS-Position (oder, wenn kein
  // GPS-Progress, um Track-Mitte). Bei Track-Anfang/-Ende wird das
  // Fenster auf die Track-Bounds geclamped, statt ins Leere zu zeigen.
  //
  // Long-Press-Cancel: wenn der User den Finger > 5 px bewegt bevor
  // 1 s vorbei sind, wird der Long-Press canceled und das Pointer-Event
  // läuft als normaler Scrub durch.
  // ---------------------------------------------------------------------
  // Wave 14ew: Zoom-Stufen aus den Rider-Settings (Default 1000/5000),
  // statt hardcoded. `km1`/`km5` sind nur noch interne Carousel-Keys —
  // die tatsächlichen Meter + die Badge-Labels kommen aus zoomNear/zoomFar.
  const [[zoomNear, zoomFar]] = useElevationZoomLevels();
  type ZoomMode = 'full' | 'km1' | 'km5';
  const ZOOM_CYCLE: ZoomMode[] = ['full', 'km1', 'km5'];
  const ZOOM_WINDOW_M: Record<ZoomMode, number> = {
    full: Infinity,
    km1: zoomNear,
    km5: zoomFar,
  };
  const [zoomMode, setZoomMode] = useState<ZoomMode>('full');
  // Wave 14cf-fix2/3: Long-Press → Doppelklick.
  // User-Feedback: 1-Sek-Hold ist beim Radfahren nicht praktikabel
  // (zittert, abrutschen, Aufmerksamkeit weg). Doppelklick ist robuster.
  // Wir tracken den letzten Pointer-Up-Zeitpunkt + Position; ein zweiter
  // Pointer-Up innerhalb von DOUBLE_TAP_MS und <DOUBLE_TAP_PX Distanz
  // triggert den Carousel-Wechsel.
  //
  // Wave 14cf-fix3: DOUBLE_TAP_PX von 30 auf 48 erhöht. Auf einem fahrenden
  // Bike ruckelt der Finger leicht; 30 px war zu eng (User-Feedback: Test
  // schlug auf staubigem Trail manchmal fehl). 48 px = ca. 12 mm
  // Toleranz auf einem 4× Retina-Display — immer noch klar als "tap" und
  // nicht "wisch" erkennbar.
  const lastTapRef = useRef<{ t: number; x: number; y: number } | null>(null);
  const DOUBLE_TAP_MS = 350;
  const DOUBLE_TAP_PX = 48;

  const cycleZoom = useCallback(() => {
    setZoomMode((prev) => {
      const idx = ZOOM_CYCLE.indexOf(prev);
      const next = ZOOM_CYCLE[(idx + 1) % ZOOM_CYCLE.length] ?? 'full';
      return next;
    });
  }, []);

  // Aktuelles Sicht-Fenster in absoluten Track-Metern. Bei full = ganze Strecke.
  // Bei km1/km5: die Position sitzt bei 10% von links — 90% des Fensters
  // zeigt nach VORNE (was kommt), 10% kurzer Blick zurück. Wave 14ex:
  // vorher 50/50 zentriert; User-Wunsch: "die schon gefahrene Hälfte
  // interessiert beim Profil nicht, ich will sehen was kommt".
  // Fallback-Center: Scrub-Pos, sonst Track-Mitte.
  // Clamp hält die Fenster-BREITE konstant (windowM) und verschiebt nur die
  // Lage — am Track-Start rückt die Position nach links (mehr "gefahren"
  // sichtbar), am Track-Ende nach rechts (weniger "kommt"), aber nie leerer
  // Raum jenseits der Track-Bounds.
  const windowM = ZOOM_WINDOW_M[zoomMode];
  let viewFromMeters = 0;
  let viewToMeters = totalMeters;
  if (Number.isFinite(windowM) && windowM < totalMeters && totalMeters > 0) {
    const center = progressMeters ?? scrubMeters ?? totalMeters / 2;
    const behind = windowM * 0.1;
    const ahead = windowM * 0.9;
    let from = center - behind;
    let to = center + ahead;
    if (from < 0) {
      to -= from;
      from = 0;
    }
    if (to > totalMeters) {
      from -= to - totalMeters;
      to = totalMeters;
    }
    viewFromMeters = Math.max(0, from);
    viewToMeters = Math.min(totalMeters, to);
  }
  const viewSpanM = Math.max(1, viewToMeters - viewFromMeters);
  const isZoomed = zoomMode !== 'full';

  // Wave 14ca-fix64: Scrub-Reveal des aktuellen Trail-Names. Wenn segments
  // gesetzt + onSegmentScrub-Callback uebergeben + scrubMeters != null,
  // finde das passende Segment und gib's an den Caller. Wir setzen das
  // hier in einem useEffect (nicht direkt im Handler) damit auch Aenderungen
  // von ausserhalb (z.B. der Caller setzt scrubMeters programmatisch)
  // den Callback triggern.
  useEffect(() => {
    if (!onSegmentScrub) return;
    if (segments && scrubMeters != null) {
      const seg = findSegmentAt(segments, scrubMeters);
      onSegmentScrub(seg);
    } else {
      onSegmentScrub(null);
    }
  }, [scrubMeters, segments, onSegmentScrub]);

  // Unmount-Cleanup: wenn der User mit aktivem Scrub einen Style-Wechsel /
  // Tab-Switch macht, wären sonst die window-Listener ewig dran.
  useEffect(() => {
    return () => {
      scrubCleanupRef.current?.();
      scrubCleanupRef.current = null;
    };
  }, []);

  // Pointer-Position → Meter vom Track-Start. Greift auf clientX zu.
  //
  // Wave 14cf: respektiert das aktive Zoom-Window. Wenn kein Zoom (full),
  // läuft die Skala 0..totalMeters; bei km1/km5 entspricht 0% pixel der
  // viewFromMeters, 100% pixel der viewToMeters. Der Caller bekommt
  // weiterhin absolute Track-Meter — Carousel ist intern.
  const pointerToMeters = (clientX: number): number | null => {
    const svg = svgRef.current;
    if (!svg || totalMeters <= 0) return null;
    const rect = svg.getBoundingClientRect();
    if (rect.width <= 0) return null;
    const x = Math.max(0, Math.min(rect.width, clientX - rect.left));
    const ratio = x / rect.width;
    return viewFromMeters + ratio * viewSpanM;
  };

  // "Bin ich ECHT auf diesem Trail?" — wenn nein, hart gaten:
  // Progress-Marker + gefahren/Rest-Labels werden unterdrückt. Ein kleiner
  // Hint-Text sagt dem User warum.
  const farFromTrack =
    offTrackMeters != null && offTrackMeters > ON_TRACK_THRESHOLD_METERS;

  // Path-Daten werden memoized damit React bei jedem progress-Update nicht
  // das ganze SVG-Polyline neu baut (nur der Marker bewegt sich).
  // Wave 14cf: Path wird jetzt nur über das aktive Zoom-Window gezeichnet
  // — viewFromMeters..viewToMeters statt der gesamten Strecke. So bleibt
  // beim 1km-Modus auch auf einem 73km-Track die Höhenmeter-Auflösung
  // sichtbar.
  const pathData = useMemo(() => {
    if (!elevations || elevations.length < 2) return null;
    if (distances.length !== elevations.length) return null;

    // Sample-Indices die ins Window fallen (inklusive je einen "Lead-In"-
    // Sample links/rechts, damit der Anfang nicht abgeschnitten aussieht).
    let firstIdx = -1;
    let lastIdx = -1;
    for (let i = 0; i < distances.length; i++) {
      const m = distances[i]!;
      if (m >= viewFromMeters && m <= viewToMeters) {
        if (firstIdx < 0) firstIdx = i;
        lastIdx = i;
      }
    }
    if (firstIdx < 0 || lastIdx < 0) return null;
    const startI = Math.max(0, firstIdx - 1);
    const endI = Math.min(elevations.length - 1, lastIdx + 1);

    // Y-Scale nur über die SICHTBAREN Samples — dann nutzt der Zoom auch
    // den vertikalen Platz aus (sonst wäre eine 50m-Höhenänderung in
    // einem 1km-Zoom genauso flach wie in der 73km-Vollansicht).
    let minE = Infinity;
    let maxE = -Infinity;
    for (let i = startI; i <= endI; i++) {
      const e = elevations[i]!;
      if (e < minE) minE = e;
      if (e > maxE) maxE = e;
    }
    if (!Number.isFinite(minE) || !Number.isFinite(maxE)) return null;
    const range = Math.max(maxE - minE, 1);
    const topPad = 0.1;
    const bottomPad = 0.1;
    const yScale = (e: number) => {
      const t = (e - minE) / range;
      return (1 - t) * (1 - topPad - bottomPad) + topPad;
    };
    // X-Scale rechnet den absoluten Distance-Wert auf 0..1 im aktuellen
    // Window: (m - viewFrom) / viewSpan. Das ist die Stelle die Long-Press
    // mathematisch unterscheidet vom alten Index-basierten Mapping.
    const xScale = (i: number) => {
      const m = distances[i] ?? 0;
      const ratio = (m - viewFromMeters) / viewSpanM;
      return Math.max(0, Math.min(1, ratio));
    };

    let line = '';
    for (let i = startI; i <= endI; i++) {
      const x = xScale(i) * 100;
      const y = yScale(elevations[i]!) * 100;
      line += (i === startI ? 'M' : 'L') + x.toFixed(2) + ',' + y.toFixed(2);
    }
    const area = line + `L100,100L0,100Z`;

    // Pro-Segment-Pfade — analog zum Default-Path eingeschränkt aufs Window.
    // Wave 14cl: zusätzlich zur Linie auch eine geschlossene Area pro
    // Segment, damit die Fläche unter dem Profil eingefärbt werden kann
    // (User-Wunsch: "ganze Fläche markieren, damit man's auch mit Sonne +
    // dreckiger Brille noch sieht").
    const colored: Array<{ d: string; color: string }> = [];
    const coloredAreas: Array<{ d: string; color: string }> = [];
    if (segments && segments.length > 0) {
      for (const seg of segments) {
        // Window-Intersection: nur der Teil des Segments der überhaupt im
        // Window sichtbar ist.
        const segFrom = Math.max(seg.fromMeters, viewFromMeters);
        const segTo = Math.min(seg.toMeters, viewToMeters);
        if (segTo <= segFrom) continue;

        let segFirst = -1;
        let segLast = -1;
        for (let i = startI; i <= endI; i++) {
          const m = distances[i]!;
          if (m >= segFrom && m <= segTo) {
            if (segFirst < 0) segFirst = i;
            segLast = i;
          }
        }
        if (segFirst < 0 || segLast < 0 || segLast <= segFirst) continue;
        const sStart = Math.max(startI, segFirst - 1);
        const sEnd = Math.min(endI, segLast + 1);
        let d = '';
        for (let i = sStart; i <= sEnd; i++) {
          const x = xScale(i) * 100;
          const y = yScale(elevations[i]!) * 100;
          d += (i === sStart ? 'M' : 'L') + x.toFixed(2) + ',' + y.toFixed(2);
        }
        colored.push({ d, color: seg.color });
        // Area: Profil-Linie + Down-to-Baseline + Close. Endet bei x des
        // letzten gezeichneten Samples zurück auf y=100 (Baseline), dann
        // unten weiter bis x des ersten Samples, dann Close.
        const xStart = xScale(sStart) * 100;
        const xEnd = xScale(sEnd) * 100;
        const areaD = `${d}L${xEnd.toFixed(2)},100L${xStart.toFixed(2)},100Z`;
        coloredAreas.push({ d: areaD, color: seg.color });
      }
    }

    // Wave 14ew / 14ew-fix: horizontale Höhen-Gridlines. Absolute Höhen-
    // Levels (…, 0, 500, 1000, …) gemappt via yScale auf die viewBox-y.
    //   - Voll-Ansicht (zoomMode 'full'): nur 500m-Linien (Major, +Label)
    //   - Gezoomt (km1/km5): 100m dünn (Minor) + 500m dick (Major, +Label)
    //
    // WICHTIG (Bug-Fix): yScale hat oben+unten je 10% Padding. Eine 500er-
    // Linie die knapp unter minE bzw. über maxE liegt ist visuell trotzdem
    // auf dem Chart (im Padding-Bereich). Im Zoom-Fenster (kleine Höhen-
    // Range) liegt die nächste 500er oft GENAU dort → wurde vorher nicht
    // generiert (Loop nur über [minE,maxE]) → Major + Label fehlten. Fix:
    // über den gepaddeten Bereich iterieren und per berechnetem y ∈ [0,100]
    // filtern (= "ist die Linie sichtbar auf dem Chart").
    const gridZoomed = zoomMode !== 'full';
    const MAJOR_STEP = 500;
    const MINOR_STEP = 100;
    const gridLines: Array<{ y: number; major: boolean; label: number }> = [];
    // 10% Pad je Seite ÷ 80% Nutzhöhe = 0.125 der Höhen-Range über/unter.
    const padOver = 0.125 * range;
    const loVis = minE - padOver;
    const hiVis = maxE + padOver;
    const pushIfVisible = (lv: number, major: boolean) => {
      const y = yScale(lv) * 100;
      if (y < 0 || y > 100) return; // außerhalb des Charts (jenseits Padding)
      gridLines.push({ y, major, label: lv });
    };
    const firstMajor = Math.ceil(loVis / MAJOR_STEP) * MAJOR_STEP;
    for (let lv = firstMajor; lv <= hiVis; lv += MAJOR_STEP) {
      pushIfVisible(lv, true);
    }
    if (gridZoomed) {
      const firstMinor = Math.ceil(loVis / MINOR_STEP) * MINOR_STEP;
      for (let lv = firstMinor; lv <= hiVis; lv += MINOR_STEP) {
        if (lv % MAJOR_STEP === 0) continue; // schon als Major-Linie
        pushIfVisible(lv, false);
      }
    }

    return { line, area, colored, coloredAreas, minE, maxE, gridLines };
  }, [
    elevations,
    distances,
    segments,
    viewFromMeters,
    viewToMeters,
    viewSpanM,
    zoomMode,
  ]);

  if (!elevations || elevations.length < 2 || !pathData) {
    if (source === 'pending') {
      return (
        <div
          className="w-full flex items-center justify-center text-[11px] text-white/50"
          style={{ height: heightPx }}
        >
          Computing elevation profile…
        </div>
      );
    }
    return (
      <div
        className="w-full flex items-center justify-center text-[11px] text-white/40"
        style={{ height: heightPx }}
      >
        No elevation data available
      </div>
    );
  }

  // Marker-Position als Prozent der aktuellen Sicht — nur wenn User WIRKLICH
  // auf dem Trail ist UND die Position überhaupt ins Sicht-Window fällt.
  // Wave 14cf: bei zoom = full ist viewFrom=0 und viewSpan=totalMeters →
  // gleiches Verhalten wie vorher. Bei km1/km5: nur wenn die GPS-Position
  // im Sicht-Fenster liegt, sonst null (Marker verschwindet kurz wenn der
  // User zwischen Window-Updates an den Rand gerät).
  let progressPct: number | null = null;
  if (!farFromTrack && progressMeters != null && totalMeters > 0) {
    if (
      progressMeters >= viewFromMeters - 0.01 &&
      progressMeters <= viewToMeters + 0.01
    ) {
      progressPct = Math.max(
        0,
        Math.min(1, (progressMeters - viewFromMeters) / viewSpanM),
      );
    }
  }
  // Rider ist "off trail" (dimmed marker) wenn >50m vom Track weg.
  const offTrack = offTrackMeters != null && offTrackMeters > 50;

  // Wave 14cf-fix4: helper für korrekte Sample-Lookup-Math.
  //
  // Vorher (BUG): scrubPct/progressPct sind WINDOW-Fraktionen (0..1 im
  // 5km-Zoom-Fenster), wurden aber als GLOBAL-Index interpretiert:
  //
  //   const idxFloat = scrubPct * (elevations.length - 1);
  //
  // Bei vollem Zoom (window = ganze Strecke) ist das richtig — Window-
  // Fraktion = Track-Fraktion. Im 5km-Zoom auf einem 51km-Track aber
  // ist das komplett verkehrt: scrubPct=0.5 lieferte Sample[1000] (also
  // Track-Meter 25.5km), egal wo der User wirklich gescrubbt hat. User-
  // Report (Bilder 1799/1801/1802): "Label zeigt 4m alt., 142m alt.,
  // 23m alt. — drei verschiedene Werte direkt nebeneinander, dabei geht
  // die Profil-Linie sauber bergauf". Genau dieser Off-by-Index-Bug.
  //
  // Fix: Window-Fraktion → Track-Meter → Global-Index.
  const sampleIndexAtPct = (pct: number): number => {
    const trackMeters = viewFromMeters + pct * viewSpanM;
    if (totalMeters <= 0) return 0;
    return (trackMeters / totalMeters) * (elevations.length - 1);
  };
  const interpolateElevationAtPct = (pct: number): number => {
    const idxFloat = sampleIndexAtPct(pct);
    const i0 = Math.max(0, Math.floor(idxFloat));
    const i1 = Math.min(i0 + 1, elevations.length - 1);
    const f = idxFloat - i0;
    return elevations[i0]! * (1 - f) + elevations[i1]! * f;
  };

  // Aktuelle Höhe ermitteln:
  //
  // Wave 14cf-fix3: GPS-Altitude bedingungslos bevorzugen, wenn verfügbar.
  // User-Bug: "Höhenprofil zeigt eine ganz andere Höhe an als die auf der
  // ich bin". Ursache: SRTM-Profil-Interpolation kann ±10–30 m vom realen
  // Wert abweichen (DEM-Auflösung 30 m, plus GPS-Snap-Drift); bei steilen
  // Pässen sind 30 m optisch sehr störend.
  //
  // Vorher: GPS wurde nur bei farFromTrack ODER (on-track & !offTrack)
  // verwendet — im Zwischenbereich 50–75 m wurde die SRTM-Interpolation
  // gezeigt obwohl GPS-Daten da waren. Dadurch sah der User in genau
  // diesem Übergangsbereich eine Höhe die mit seiner Smartwatch/seinem
  // Garmin nicht zusammenpasste.
  //
  // Jetzt: wenn das Geolocation-API einen `altitude` liefert, nehmen wir
  // den ALWAYS — die ist auch bei modernen Phones (10–30 m Genauigkeit)
  // näher am Wahren als ein 30-m-DEM-Sample. Profil-Interpolation ist nur
  // noch Fallback wenn GPS gar keine Höhe liefert UND der User auf dem
  // Track ist.
  let currentAltitude: number | null = null;
  if (gpsAltitude != null && Number.isFinite(gpsAltitude)) {
    currentAltitude = gpsAltitude;
  } else if (!farFromTrack && progressPct != null) {
    currentAltitude = interpolateElevationAtPct(progressPct);
  }

  // Scrub-Cursor-Position als 0..1 in der aktuellen Sicht. Beim Zoom liegt
  // scrubMeters per Definition immer im Window (pointerToMeters clamped),
  // also kein extra Out-of-Range-Gate nötig.
  const scrubPct =
    scrubMeters != null && totalMeters > 0
      ? Math.max(0, Math.min(1, (scrubMeters - viewFromMeters) / viewSpanM))
      : null;

  // Höhe an der Scrub-Position aus dem Profil interpolieren. Wave 14cf-
  // fix4 — siehe sampleIndexAtPct: window-fraktion korrekt in Track-
  // Meter umrechnen damit der Label-Wert mit dem zoom-gerenderten Pfad
  // konsistent ist (Profil wird mit Track-Meter-X gerendert, nicht mit
  // sample-index-X).
  const scrubAltitudeRaw =
    scrubPct != null ? interpolateElevationAtPct(scrubPct) : null;
  // Wave 14dr: Everest-Scrub-Label-Override. Wenn der User auf das
  // Easter-Egg-Profil scrubt und sich der Gipfel-Nähe ≥95 % der Track-
  // Länge nähert, zeigen wir 8850 m statt des interpolierten Samples.
  // Sonst würde der DEM-Underestimate (8685) am rechten Rand sichtbar
  // werden, sobald der Finger den Gipfel berührt.
  const scrubAltitude =
    isEverest && scrubPct != null && scrubPct >= 0.95
      ? 8850
      : scrubAltitudeRaw;
  // Distanz vom Trailanfang in m/km-Format — User-Wunsch Batch 7:
  //   <1 km   → "650 m dist."
  //   ≥1 km   → "3.54 km dist."
  const formatScrubDistance = (m: number): string =>
    m < 1000 ? `${Math.round(m)} m dist.` : `${(m / 1000).toFixed(2)} km dist.`;
  const scrubDistText =
    scrubMeters != null ? formatScrubDistance(Math.max(0, Math.min(totalMeters, scrubMeters))) : null;
  const scrubAltText =
    scrubAltitude != null ? `${Math.round(scrubAltitude)} m alt.` : null;

  // Scrub-Gesten-Handling mit window-level Listenern:
  //
  // Das ursprüngliche Design (setPointerCapture + onPointerMove auf SVG)
  // hat auf iOS Safari mehrere Edge-Cases:
  //   1. setPointerCapture auf SVGSVGElement ist in Safari bis iOS 17
  //      unzuverlässig — touch-Pointer werden manchmal gar nicht gecaptured,
  //      dann verliert das SVG den pointer beim kleinsten Move.
  //   2. Selbst mit touch-action:none landen manche Finger-Gesten ins Parent-
  //      Scroll, sobald der Finger minimal aus der SVG-Bounding-Box rutscht.
  //   3. onPointerLeave feuert frühzeitig und beendet den Scrub obwohl der
  //      User den Finger noch unten hat.
  //
  // Lösung: Handler am Wrapper-Div (HTMLElement, robust auf iOS) anmelden,
  // und sobald pointerdown kommt, pointermove/up/cancel auf WINDOW horchen.
  // Window-Listener sehen jeden Move egal wo der Finger hingeht — keine
  // Capture-Bugs möglich. Damit iOS den Scroll-Hijack nicht startet: die
  // move-Listener sind explizit non-passive ({ passive: false }) + wir rufen
  // preventDefault() um die Parent-Scroll-Gesture zu stoppen.
  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!onScrub) return;
    // Nur primärer Button / einzelner Finger — sonst kriegen wir bei Pinch
    // doppelte Events.
    if (e.isPrimary === false) return;

    scrubActiveRef.current = true;
    const initial = pointerToMeters(e.clientX);
    if (initial != null) onScrub(initial);

    // Wave 14cf-fix2 — Doppelklick statt Long-Press für Zoom-Carousel.
    //
    // Beim Radfahren ist 1-Sek-Halten nicht praktikabel. Stattdessen:
    // zwei Pointer-Ups innerhalb von 350 ms am gleichen Ort (< 30 px)
    // = Double-Tap → Carousel cycelt. Wir messen die Bewegung während
    // des Drücks: wenn dx²+dy² > THRESH ist's ein normaler Scrub und
    // der "second-tap"-Slot wird gecanceled.
    const startX = e.clientX;
    const startY = e.clientY;
    let movedFar = false;

    const onMove = (ev: PointerEvent) => {
      if (!scrubActiveRef.current) return;
      if (ev.cancelable) ev.preventDefault();
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;
      if (dx * dx + dy * dy > DOUBLE_TAP_PX * DOUBLE_TAP_PX) {
        movedFar = true;
      }
      const m = pointerToMeters(ev.clientX);
      if (m != null) onScrub(m);
    };
    const onEnd = () => {
      if (!scrubActiveRef.current) return;
      scrubActiveRef.current = false;
      cleanup();
      onScrub(null);
      // Doppelklick-Detection nur wenn der User innerhalb dieses Drücks
      // KAUM bewegt hat (sonst ist's ein scrub-Wisch, kein tap).
      if (!movedFar) {
        const now = Date.now();
        const last = lastTapRef.current;
        if (
          last &&
          now - last.t < DOUBLE_TAP_MS &&
          Math.abs(startX - last.x) < DOUBLE_TAP_PX &&
          Math.abs(startY - last.y) < DOUBLE_TAP_PX
        ) {
          // Erfolgreicher Doppelklick.
          cycleZoom();
          lastTapRef.current = null;
        } else {
          lastTapRef.current = { t: now, x: startX, y: startY };
        }
      } else {
        lastTapRef.current = null;
      }
    };
    const cleanup = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onEnd);
      window.removeEventListener('pointercancel', onEnd);
      scrubCleanupRef.current = null;
    };
    // Vorherigen Scrub kaputt-räumen, falls pointerdown ohne pointerup kam.
    scrubCleanupRef.current?.();
    scrubCleanupRef.current = cleanup;

    // { passive: false } erlaubt preventDefault() im Move-Handler — essentiell
    // damit iOS keine Parent-Scroll-Geste startet.
    window.addEventListener('pointermove', onMove, { passive: false });
    window.addEventListener('pointerup', onEnd);
    window.addEventListener('pointercancel', onEnd);
  };

  // iOS Long-Press Hell: ohne diese CSS-Properties feuert iOS Safari nach
  // ~500ms gehaltenem Finger das native "Copy/Lookup/Share"-Callout — das
  // unterbricht den Scrub und nervt brutal. Wir müssen ALLE drei dichtmachen:
  //   - userSelect / WebkitUserSelect: kein Text-Selection-Highlight, kein Menü
  //   - WebkitTouchCallout: kein Long-Press-Callout
  //   - WebkitTapHighlightColor: kein blauer iOS-Tap-Flash beim Drag-Start
  // touch-action:none verhindert Pan/Zoom-Konkurrenz mit der Karte/dem Sheet.
  // onContextMenu.preventDefault: Desktop-Right-Click + iOS-Backup, falls
  // doch ein Callout durchschlüpft.
  const noSelectStyle: CSSProperties | undefined = onScrub
    ? {
        touchAction: 'none',
        cursor: 'ew-resize',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        WebkitTouchCallout: 'none',
        WebkitTapHighlightColor: 'transparent',
      }
    : undefined;

  return (
    <div
      ref={wrapperRef}
      className="w-full"
      style={noSelectStyle}
      // Handler am Wrapper-Div — deutlich stabiler auf iOS als am SVG.
      // Once pointerdown kommt, übernehmen window-Listener die Move/Up-
      // Events, sodass der Scrub auch dann weiterläuft wenn der Finger
      // die Profile-Box verlässt.
      onPointerDown={onScrub ? handlePointerDown : undefined}
      onContextMenu={onScrub ? (e) => e.preventDefault() : undefined}
    >
      <div className="relative">
      {/* Wave 14cf: Zoom-Badge oben links — zeigt dass der User gerade im
          gezoomten Modus ist. In der Vollansicht (full) verstecken wir's
          komplett damit das Profil clean bleibt. Tap am Badge cycelt
          ebenfalls (alternativer Weg statt Long-Press). */}
      {isZoomed && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            cycleZoom();
          }}
          className="absolute z-20 left-1.5 top-1.5 px-2 py-0.5 rounded-md bg-orange-500/85 text-white text-[10px] font-semibold uppercase tracking-wider shadow-md pointer-events-auto"
          aria-label="Cycle zoom level"
          style={{ touchAction: 'manipulation' }}
        >
          {zoomMode === 'km1'
            ? formatZoomLabel(zoomNear)
            : formatZoomLabel(zoomFar)}
        </button>
      )}
      {/* Wave 14ew: Major-Gridline-Labels als HTML-Overlay (NICHT im SVG —
          das ist preserveAspectRatio=none, SVG-Text würde horizontal/vertikal
          verzerrt). top:% bezieht sich auf die relative-Box = SVG-Höhe.
          pointer-events:none, damit der Scrub nicht blockiert wird. */}
      {pathData.gridLines
        ?.filter((g) => g.major)
        .map((g, i) => (
          <span
            key={`grid-label-${i}`}
            className="absolute z-10 left-1 text-[9px] text-white/40 tabular-nums pointer-events-none"
            style={{ top: `${g.y}%`, transform: 'translateY(-50%)' }}
          >
            {g.label} m
          </span>
        ))}
      <svg
        ref={svgRef}
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        width="100%"
        height={heightPx}
        className="block"
        style={noSelectStyle}
      >
        {/* Wave 14ew: horizontale Höhen-Gridlines GANZ ZUERST (hinter Area +
            Profil-Linie). Major (500m) dicker/heller, Minor (100m) dünn/dimm.
            non-scaling-stroke hält die Breite trotz preserveAspectRatio. */}
        {pathData.gridLines?.map((g, i) => (
          <line
            key={`grid-${i}`}
            x1={0}
            x2={100}
            y1={g.y}
            y2={g.y}
            stroke="#ffffff"
            strokeOpacity={g.major ? 0.35 : 0.18}
            strokeWidth={g.major ? 1 : 0.5}
            vectorEffect="non-scaling-stroke"
          />
        ))}

        {/* Gefüllte Area unter der Linie. Wave 14cl: bei segments-Prop
            rendern wir per-Segment farbige Areas (höhere Opazität, damit
            sie auch bei Sonneneinstrahlung + Sonnenbrille auffällt) statt
            der monochromen teal-Füllung. Default-Area fungiert als Backdrop
            für nicht-zugewiesene Bereiche (Connector/Bridge). */}
        <path d={pathData.area} fill="#14b8a6" fillOpacity={0.15} />
        {pathData.coloredAreas && pathData.coloredAreas.length > 0 && (
          <>
            {pathData.coloredAreas.map((seg, i) => (
              <path
                key={`area-${i}`}
                d={seg.d}
                fill={seg.color}
                fillOpacity={0.4}
              />
            ))}
          </>
        )}

        {/* Profil-Linie selbst. vector-effect:non-scaling-stroke hält die
            Stroke-Breite unabhängig vom preserveAspectRatio-Stretch konstant.
            Wave 14ca-fix64: bei segments-Prop rendern wir die Default-Linie
            duenner + dimmer und legen die per-Segment-farbigen Sub-Paths
            DARUEBER. Sub-Paths decken nur die Bereiche ab die einem Segment
            zugeordnet sind; Connector/Bridge-Bereiche behalten die
            Default-teal-Linie. */}
        {pathData.colored && pathData.colored.length > 0 ? (
          <>
            <path
              d={pathData.line}
              fill="none"
              stroke="#14b8a6"
              strokeWidth={1.5}
              strokeOpacity={0.5}
              vectorEffect="non-scaling-stroke"
            />
            {pathData.colored.map((seg, i) => (
              <path
                key={i}
                d={seg.d}
                fill="none"
                stroke={seg.color}
                strokeWidth={3}
                strokeLinecap="round"
                vectorEffect="non-scaling-stroke"
              />
            ))}
          </>
        ) : (
          <path
            d={pathData.line}
            fill="none"
            stroke="#14b8a6"
            strokeWidth={2}
            vectorEffect="non-scaling-stroke"
          />
        )}

        {progressPct != null && (
          <g>
            {/* Vertikale Linie als Marker */}
            <line
              x1={progressPct * 100}
              x2={progressPct * 100}
              y1={0}
              y2={100}
              stroke={offTrack ? '#f59e0b' : '#ef4444'}
              strokeWidth={1.5}
              vectorEffect="non-scaling-stroke"
              strokeOpacity={offTrack ? 0.7 : 1}
            />
            {/* Dot auf der Profil-Linie selbst — Höhe aus aktuellem Sample.
                Wave 14cf-fix4: elevation kommt PRECOMPUTED rein, damit der
                Dot exakt auf dem Pfad-Y-Wert sitzt (gleiche Window→Track-
                Meter-Math wie pathData). Vorher sample-Index-Bug bei Zoom. */}
            <ProgressDot
              progressPct={progressPct}
              elevation={interpolateElevationAtPct(progressPct)}
              minE={pathData.minE}
              maxE={pathData.maxE}
              offTrack={offTrack}
            />
          </g>
        )}

        {/* Scrub-Cursor: weiße Linie + weißer Dot — visuell abgegrenzt vom
            roten Progress-Marker, damit der User sofort sieht "das ist meine
            Finger-Position, nicht mein GPS". */}
        {scrubPct != null && (
          <g>
            <line
              x1={scrubPct * 100}
              x2={scrubPct * 100}
              y1={0}
              y2={100}
              stroke="#ffffff"
              strokeWidth={1.5}
              vectorEffect="non-scaling-stroke"
              strokeOpacity={0.9}
            />
            <ProgressDot
              progressPct={scrubPct}
              elevation={interpolateElevationAtPct(scrubPct)}
              minE={pathData.minE}
              maxE={pathData.maxE}
              offTrack={false}
              fill="#ffffff"
              stroke="#0d9488"
            />
          </g>
        )}
      </svg>

      {/* Schlüsselstellen: Overlay MIT eigenem Aspect-Ratio, weil das Profil-SVG
          preserveAspectRatio="none" benutzt (stretcht Koordinaten auf die Box-
          Breite). In dem stretched Koordinatensystem würden die Dreiecke
          gequetscht aussehen. Stattdessen: absolut positionierte HTML-<span>s
          mit left:<pct>% und einem fixed-size <HazardTriangleIcon>. Der Icon
          hat sein eigenes SVG mit quadratischem viewBox → rendert immer gleich
          wie auf der Map. translate(-50%) zentriert horizontal, top:2px hält
          es dicht am Top-Pad des Profils (analog zur alten cy=8 Position).
          Clamp distanceMeters auf Track-Länge für pathologische Admin-Werte. */}
      {cruxPoints && cruxPoints.length > 0 && totalMeters > 0 && (
        <div
          className="absolute inset-x-0 top-0 pointer-events-none select-none"
          aria-hidden="true"
        >
          {cruxPoints.map((c, i) => {
            // Wave 14cf: Cruxes nur rendern wenn sie ins Sicht-Window fallen,
            // und Position relativ zum Window berechnen statt zur Gesamt-
            // Strecke. Sonst landen alle Cruxes bei pct=0..1 auf Total-Skala
            // und im 1km-Zoom liegen sie alle auf einem Punkt.
            if (
              c.distanceMeters < viewFromMeters - 0.01 ||
              c.distanceMeters > viewToMeters + 0.01
            ) {
              return null;
            }
            const pct = Math.max(
              0,
              Math.min(1, (c.distanceMeters - viewFromMeters) / viewSpanM),
            );
            // Tooltip mit Distanz + Höhe (falls gesetzt). Höhe ist User-Wunsch:
            // "sportler haben smartwatches/bike computer und höhe wird
            // angezeigt" — der Desktop-Hover-Tooltip hilft bei Planung.
            const distLabel =
              c.distanceMeters < 1000
                ? `${Math.round(c.distanceMeters)} m`
                : `${(c.distanceMeters / 1000).toFixed(2)} km`;
            const eleLabel =
              c.elevationMeters != null ? ` · ${Math.round(c.elevationMeters)} m alt.` : '';
            const notePrefix = c.note ? `${c.note} · ` : 'Key section · ';
            const title = `${notePrefix}${distLabel} from start${eleLabel}`;
            return (
              <span
                key={i}
                className="absolute"
                style={{
                  left: `${pct * 100}%`,
                  top: 2,
                  transform: 'translateX(-50%)',
                  lineHeight: 0,
                }}
                title={title}
              >
                <HazardTriangleIcon size={14} />
              </span>
            );
          })}
        </div>
      )}

      {/* Scrub-Labels: links vom weißen Strich die Distanz, rechts die Höhe.
          User-Wunsch Batch 7: "rechts von dem weissen strich die höhe [345m alt.]
          und links von dem weissen balken die entfernung vom trailanfang
          [650m dist. / 3.54km dist.]". Positioniert mit left:<pct>% und
          translateX(-100%) bzw. 0%, plus kleiner Gap. Bei sehr niedrigem/hohem
          scrubPct würden die Labels links/rechts rausragen — wir clampen den
          Anker so, dass sie immer noch lesbar sind, indem wir near-edges die
          Labels gegeneinander pinnen. */}
      {scrubPct != null && (scrubDistText || scrubAltText) && (
        <div
          className="absolute inset-x-0 top-1 pointer-events-none select-none"
          aria-hidden="true"
        >
          {scrubDistText && (
            <div
              // Wave 14dp: Inline-Style für color + background damit der
              // light-mode CSS-Override (text-white → slate, bg-black/65
              // → fast-transparent) NICHT greift. Die Labels schweben über
              // dem Höhenprofil das in beiden Themes farbig ist — weisser
              // Text auf dunklem Halo bleibt überall gut lesbar.
              className="absolute text-[10px] leading-none font-medium rounded px-1 py-0.5 whitespace-nowrap tabular-nums"
              style={{
                color: '#ffffff',
                background: 'rgba(0, 0, 0, 0.72)',
                ...(scrubPct < 0.18
                  ? { left: `calc(${scrubPct * 100}% + 6px)`, top: '14px' }
                  : { left: `calc(${scrubPct * 100}% - 6px)`, transform: 'translateX(-100%)' }),
              }}
            >
              {scrubDistText}
            </div>
          )}
          {scrubAltText && (
            <div
              className="absolute text-[10px] leading-none font-medium rounded px-1 py-0.5 whitespace-nowrap tabular-nums"
              style={{
                color: '#ffffff',
                background: 'rgba(0, 0, 0, 0.72)',
                ...(scrubPct > 0.82
                  ? {
                      left: `calc(${scrubPct * 100}% - 6px)`,
                      transform: 'translateX(-100%)',
                      top: '14px',
                    }
                  : { left: `calc(${scrubPct * 100}% + 6px)` }),
              }}
            >
              {scrubAltText}
            </div>
          )}
        </div>
      )}
      </div>

      {showDistanceLabels && (
        <div className="mt-1 px-0.5 tabular-nums">
          {/* Einzeiliges Layout — Start-Höhe links, aktuelle Höhe Mitte,
              End-Höhe rechts.
              Wave 14bk-fix: Vorher war das max/min — das hat nur für reine
              Downhills visuell gepasst (Chart fällt oben-links → unten-rechts,
              da war max=links=Start und min=rechts=Ende passend). Für Uphills
              wie Dondella Climb (4.34 km, ↑583 m) war's invertiert: Chart
              steigt links→rechts, aber Labels hatten "1173m | … | 814m" —
              konnten den User zurecht für verwirrend halten.
              Start/End-Höhe ist immer 1:1 mit den Chart-Enden konsistent,
              egal ob Up/Down/Mixed. elevations[0] = linkes Chart-Ende,
              elevations[last] = rechtes Chart-Ende.

              Sobald wir Progress haben (on-trail + Ride-Mode), ersetzen wir
              Start/End durch "km done" / "km left" — unterwegs ist die
              Resterei relevanter als die Trail-Anker.
              Off-trail-Hinweis wandert als kleine Zeile unter die Höhe in die
              Mitte — wir wollen trotzdem sichtbar machen dass der User nicht
              auf dem Trail ist, ohne eine ganze Zeile dafür zu opfern. */}
          <div className="flex justify-between items-center text-[11px] text-white/70 gap-2">
            <span className="flex-1">
              {progressPct != null
                ? `${(progressMeters! / 1000).toFixed(2)} km done`
                : `${Math.round(elevations[0]!)} m`}
            </span>

            <span className="flex flex-col items-center justify-center gap-0.5 min-w-0">
              {currentAltitude != null ? (
                <span className="font-bold text-white text-[16px] leading-none">
                  {Math.round(currentAltitude)}
                  <span className="font-normal text-white/70 text-[11px] ml-0.5">m</span>
                </span>
              ) : (
                <span className="text-white/50 text-[13px] leading-none">— m</span>
              )}
              {farFromTrack && (
                <span className="text-[10px] leading-none text-amber-400/80 whitespace-nowrap">
                  off trail ·{' '}
                  {offTrackMeters! < 1000
                    ? `${Math.round(offTrackMeters!)} m`
                    : `${(offTrackMeters! / 1000).toFixed(1)} km`}
                </span>
              )}
            </span>

            <span className="flex-1 text-right">
              {progressPct != null
                ? `${((totalMeters - progressMeters!) / 1000).toFixed(2)} km left`
                : isEverest
                  ? '8850 m'
                  : `${Math.round(elevations[elevations.length - 1]!)} m`}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Kleiner Dot exakt auf der Profillinie. Y-Position wird aus dem
 * (precomputed) elevation-Wert linear ins Min/Max-Range gemappt.
 *
 * Wave 14cf-fix4: Vorher rechnete der Dot selbst mit
 * `progressPct * (elevations.length - 1)` was im Zoom-Modus den falschen
 * Sample-Index lieferte (siehe interpolateElevationAtPct im Parent).
 * Jetzt: Parent berechnet die Höhe korrekt (Window→Track→Sample-Math) und
 * reicht sie als Prop rein. Der Dot rendert nur noch das Y-Mapping.
 */
function ProgressDot({
  progressPct,
  elevation,
  minE,
  maxE,
  offTrack,
  fill,
  stroke,
}: {
  progressPct: number;
  /** Höhe in Metern an der scrub-/progress-Position. Vom Parent korrekt
   *  berechnet (Window→Track→Sample-Index). */
  elevation: number;
  minE: number;
  maxE: number;
  offTrack: boolean;
  /** Optional Override-Farbe für den Dot (z. B. weiß für den Scrub-Cursor). */
  fill?: string;
  /** Optional Override-Farbe für den Stroke (z. B. teal für den Scrub-Cursor). */
  stroke?: string;
}) {
  const range = Math.max(maxE - minE, 1);
  const topPad = 0.1;
  const bottomPad = 0.1;
  const tY = (elevation - minE) / range;
  const y = ((1 - tY) * (1 - topPad - bottomPad) + topPad) * 100;

  return (
    <circle
      cx={progressPct * 100}
      cy={y}
      r={2.2}
      fill={fill ?? (offTrack ? '#f59e0b' : '#ef4444')}
      stroke={stroke ?? '#fff'}
      strokeWidth={0.8}
      vectorEffect="non-scaling-stroke"
    />
  );
}
